import React from "react";

/**
 * Client-side pagination control for tables.
 *
 * Deliberately built from <button> elements rather than the <a>-based
 * `components/ui/pagination.jsx`: an anchor click would trigger a full document
 * load, which drops the in-memory access token and bounces the user to /login.
 */
export function TablePagination({
  page = 0,
  pageCount = 1,
  total = 0,
  pageSize = 25,
  pageSizeOptions = [25, 50, 100],
  onPage,
  onPageSize,
  idPrefix = "pagination",
  noun = "row",
  nounPlural,
}) {
  const pages = Math.max(1, pageCount || 1);
  const safePage = Math.min(Math.max(0, page), pages - 1);
  const from = total === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(total, (safePage + 1) * pageSize);

  // Appending "s" is wrong for irregular nouns ("entrys"), so callers can
  // supply the plural explicitly.
  const plural = nounPlural || `${noun}s`;

  return (
    <div
      className="flex items-center justify-between gap-3 flex-wrap mt-4 text-xs text-gray-500"
      data-testid={`${idPrefix}-pagination`}
    >
      <span className="num">
        {total === 0
          ? `No ${plural}`
          : `Showing ${from}\u2013${to} of ${total} ${total === 1 ? noun : plural}`}
      </span>

      <div className="flex items-center gap-2">
        {onPageSize && pageSizeOptions.length > 0 && (
          <label className="flex items-center gap-1.5">
            <span className="hidden sm:inline">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
              className="h-8 px-2 border border-[var(--border)] rounded-md bg-white text-xs"
              data-testid={`${idPrefix}-size`}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}

        <button
          type="button"
          onClick={() => onPage && onPage(safePage - 1)}
          disabled={safePage === 0 || !onPage}
          className="px-3 h-8 border border-[var(--border)] rounded-md disabled:opacity-40 hover:bg-gray-50"
          data-testid={`${idPrefix}-prev`}
        >
          Previous
        </button>

        <span className="num whitespace-nowrap" data-testid={`${idPrefix}-page`}>
          Page {safePage + 1} of {pages}
        </span>

        <button
          type="button"
          onClick={() => onPage && onPage(safePage + 1)}
          disabled={safePage >= pages - 1 || !onPage}
          className="px-3 h-8 border border-[var(--border)] rounded-md disabled:opacity-40 hover:bg-gray-50"
          data-testid={`${idPrefix}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default TablePagination;